import { type FC } from "react";
import { ArrowRight, Check, Clock, Search, Gem, Landmark } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { LAWYER_PLANS } from "@/shared/constants/lawyerPlans";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";

interface ClientInvitePlanStepProps {
  planKey: LawyerPlanKey;
  priceCents: number | null;
  lawyerName: string;
  onContinue: () => void;
  onSeeAddons: () => void;
}

interface PlanContent {
  description: string;
  savingsIcon: FC<{ className?: string }>;
  savingsText: string;
  features: string[];
  strikethroughFormat: (fullPrice: number) => string;
  footerFormat: (name: string) => string;
}

const PLAN_CONTENT: Record<LawyerPlanKey, PlanContent> = {
  estate_setup: {
    description:
      "Elayne organizes everything the court and your attorney will need, from documents to family details, in one shared record",
    savingsIcon: Clock,
    savingsText: "Save 30 hours on average getting to filing",
    features: [
      "One shared workspace, so you and [lawyer] see the same record",
      "25+ admin tasks handled for you, like credit freezes, mail, and notifications",
      "Names, dates, and accounts pulled from your documents, no retyping",
    ],
    strikethroughFormat: (price) => `$${price.toLocaleString()} value`,
    footerFormat: (name) => `${name} arranged this price for you`,
  },
  asset_discovery: {
    description:
      "Search beyond public records for financial accounts, insurance policies, benefits, and property families often don't know exist",
    savingsIcon: Search,
    savingsText: "$5M+ found with Elayne Asset Search",
    features: [
      "Elayne Asset Search across 100+ government and financial sources",
      "25+ admin tasks handled for you, from credit freezes to mail forwarding",
      "One estate record you and [lawyer] work from together",
    ],
    strikethroughFormat: (price) => `$${price.toLocaleString()} value`,
    footerFormat: (name) => `${name} arranged this price for you`,
  },
  estate_administration: {
    description:
      "Move every asset to the people meant to have it, with the estate account, transfers, and final steps managed in one place",
    savingsIcon: Gem,
    savingsText: "Comparable concierge plans average $14,700",
    features: [
      "Banks and institutions contacted for you to confirm values and beneficiaries",
      "Help opening an estate bank account, with the EIN application handled",
      "One estate record you and [lawyer] work from together",
    ],
    strikethroughFormat: (price) => `Regular $${price.toLocaleString()}`,
    footerFormat: (name) => `${name} arranged this price for you`,
  },
};

export const ClientInvitePlanStep: FC<ClientInvitePlanStepProps> = ({
  planKey,
  priceCents,
  lawyerName,
  onContinue,
  onSeeAddons,
}) => {
  const plan = LAWYER_PLANS[planKey];
  const content = PLAN_CONTENT[planKey];
  const SavingsIcon = content.savingsIcon;
  const fullPriceCents = plan.price * 100;
  const hasDiscount = priceCents !== null && priceCents < fullPriceCents;
  const displayPrice =
    priceCents !== null
      ? `$${(priceCents / 100).toLocaleString()}`
      : `$${plan.price.toLocaleString()}`;
  const features = content.features.map((f) => f.replace("[lawyer]", lawyerName));

  return (
    <div className="flex h-screen bg-subtle">
      <div className="relative flex flex-1 flex-col items-center overflow-y-auto p-4 pt-16 sm:justify-center sm:p-10">
        <img src={logoFull} alt="Elayne" className="absolute left-4 top-4 h-8 sm:left-8 sm:top-8" />

        <div className="flex w-full max-w-[600px] flex-col items-center gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h2>Your attorney&apos;s recommendation</h2>
            <p className="text-sm text-secondary">
              Most families pay from the estate, not out of pocket
            </p>
          </div>

          <div className="w-full overflow-hidden rounded-lg border border-brand bg-white">
            <div className="flex flex-col gap-6 p-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-xl font-semibold">{plan.name}</h3>
                      <p className="text-sm text-secondary">{content.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <SavingsIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                      <span className="text-sm font-medium text-brand">{content.savingsText}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-xl font-semibold">{displayPrice}</span>
                    {hasDiscount && (
                      <span className="text-sm text-secondary line-through">
                        {content.strikethroughFormat(plan.price)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="h-px w-full bg-stone-200" />

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">What you get</span>
                <div className="flex flex-col gap-2">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                      <span className="text-sm text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-primary bg-background px-6 py-4">
              <span className="text-sm text-secondary">{content.footerFormat(lawyerName)}</span>
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-3">
            <Button variant="brand" rounded onClick={onContinue} className="w-full">
              Continue to payment
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
            <button
              type="button"
              onClick={onSeeAddons}
              className="text-sm font-medium text-secondary"
            >
              See add-ons
            </button>
          </div>

          <div className="flex w-full flex-col gap-2 rounded-lg border border-primary bg-background p-4">
            <div className="flex items-center gap-2">
              <Landmark className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-sm font-medium">You can pay from the estate</span>
            </div>
            <p className="text-sm text-secondary">
              Elayne is a qualified estate expense. If the estate already has a bank account, you
              can pay from it directly. If not, pay now and we can help you get reimbursed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
